/**
 * HowItWorks — premium multi-color protocol strip
 * ---------------------------------------------------------------------------
 * Matches the /tools page aesthetic: each of the 4 steps gets its own accent
 * colour, border-2 tile, ambient tint, 160deg gradient, and a coloured icon
 * chip. Number stays huge in the corner but muted so the icon leads the eye.
 */
import React from 'react';
import { MessageSquare, Lock, Cpu, BadgeCheck, Shield, Zap, Send, Star } from 'lucide-react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const ICONS = { MessageSquare, Lock, Cpu, BadgeCheck, Shield, Zap, Send, Star };

// Cycled per step so the row reads as a spectrum, not a monotone column.
const STEP_COLORS = ['#00ff9d', '#4de0ff', '#c084fc', '#ffd34d'];

const HowItWorks = () => {
  const { config } = useSiteConfig();
  const steps = config.howSteps || [];
  return (
    <section className="relative py-16 sm:py-22 overflow-hidden">
      {/* Ambient blobs — matches /tools page vibe */}
      <div aria-hidden className="pointer-events-none absolute -top-24 -left-16 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: '#00ff9d' }} />
      <div aria-hidden className="pointer-events-none absolute top-40 right-0 w-72 h-72 rounded-full opacity-15 blur-3xl" style={{ background: '#c084fc' }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-24 left-1/3 w-72 h-72 rounded-full opacity-15 blur-3xl" style={{ background: '#4de0ff' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-14">
          <div className="eh-kicker justify-center mb-3">// PROTOCOL</div>
          <h2 className="eh-display font-black leading-tight" style={{ fontSize: 'clamp(1.8rem, 5.5vw, 3.4rem)' }}>
            HOW IT <span className="eh-neon">WORKS</span>
          </h2>
          <p className="opacity-75 mt-4 max-w-lg mx-auto text-sm leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
            A 4-step protocol designed for speed, secrecy and zero drama.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-5">
          {steps.map((s, i) => {
            const I = ICONS[s.icon] || Shield;
            const c = STEP_COLORS[i % STEP_COLORS.length];
            return (
              <div
                key={s.n}
                className="hiw-tile relative rounded-2xl border-2 p-5 sm:p-6 overflow-hidden"
                style={{
                  borderColor: `${c}55`,
                  background: `linear-gradient(160deg, ${c}0c 0%, transparent 65%)`,
                  '--c': c,
                }}
                data-testid={`hiw-step-${s.n}`}
              >
                {/* Top accent stripe */}
                <span aria-hidden className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${c}, transparent)` }} />

                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${c}1a`, border: `1px solid ${c}55`, color: c }}>
                    <I size={18} strokeWidth={1.8} />
                  </div>
                  <span className="font-black text-3xl sm:text-4xl leading-none opacity-25" style={{ color: c, fontFamily: 'Cinzel, serif' }}>{s.n}</span>
                </div>

                <div className="eh-mono text-[10px] tracking-widest opacity-70 mb-1" style={{ color: c }}>// STEP · {s.n}</div>
                <div className="font-bold text-[15px] leading-snug mb-1.5" style={{ fontFamily: 'Inter,sans-serif' }}>{s.t}</div>
                <p className="text-[13px] opacity-75 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>{s.d}</p>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .hiw-tile { transition: transform .35s cubic-bezier(.2,.9,.3,1), border-color .25s, box-shadow .35s; }
        .hiw-tile:hover { transform: translateY(-3px); border-color: var(--c) !important; box-shadow: 0 22px 60px -22px color-mix(in srgb, var(--c) 55%, transparent); }
      `}</style>
    </section>
  );
};
export default HowItWorks;
